// Third Party Library
import { Badge, Typography, Tooltip } from "antd";

// Constant Library
import { ROOT_CONSTANT, TEMPLATE } from "../..";

// Components Library
import { ButtonLink } from "../../../components";

// Handlers Library
import { filter } from "../../../handlers";

// Service Library
import { TIME } from "../../../services/util";

const { TABLE_BILLING_TITLE, TABLE_BILLING_INDEX, BILLING_STATUS_TITLE } =
  TEMPLATE.BILLING;

const { icon_comment, icon_attch_excel, icon_pdf, icon_history } =
  ROOT_CONSTANT.IMG.ICON;
const { parseTimeMin, parseDate, parseMonth, convertTimeFormat } = TIME;
const { searchHDLR, filterHDLR } = filter;
const { Text } = Typography;

export const BILLING_SUMMARY_COL = ({
  redux = {},
  func = {},
  exportState = false,
}) => {
  const { user, company = {}, billings, billingsCoid } = redux;
  const { handleSearch, DrawerView, DrawerManageReceipt, DrawerRemark } = func;

  const isUserA = user?.uroleid === "A";

  const filterBillingMonthData = user.uroleid === "A" ? billings : billingsCoid;

  const columns = [
    {
      title: TABLE_BILLING_TITLE.BillingID,
      key: TABLE_BILLING_INDEX.BillingID,
      width: 180,
      fixed: "left",
      render: (record) => {
        const { billid } = record;
        return exportState ? billid : <>{billid} </>;
      },
      ...searchHDLR.getColumnSearchProps("billid", handleSearch),
    },
    {
      title: TABLE_BILLING_TITLE.Status,
      key: TABLE_BILLING_INDEX.Status,
      fixed: "left",
      render: (record) => {
        const { status } = record;
        const { name, colorTag, bgColor } = BILLING_STATUS_TITLE[status];
        return exportState ? (
          name
        ) : (
          <div
            style={{
              backgroundColor: bgColor,
              display: "inline-block",
              padding: "3px 13px",
              borderRadius: "30px",
            }}
          >
            <Badge
              color={colorTag}
              text={<span style={{ color: colorTag }}>{name}</span>}
            />
          </div>
        );
      },
      filters: filterHDLR.getColumnsFilterStatus(BILLING_STATUS_TITLE),
      onFilter: (value, record) => record.status === value,
    },
    ...(user.uroleid === "A"
      ? [
          {
            title: TABLE_BILLING_TITLE.CoName,
            key: TABLE_BILLING_INDEX.CoName,
            render: (record) => {
              const { company_name, coid } = record;

              const comp = `${company_name || ''}${company_name && coid ? ' /' : ''}${coid || ''}`;
              return exportState ? company_name : <Text>{comp}</Text>;
            },
            filters: filterHDLR.getColumnsFilterType(company),
            onFilter: (value, record) => value.includes(record.coid),
            filterSearch: true,
          },
          ...(exportState
                ? [
                    {
                      title: TABLE_BILLING_TITLE.CoID,
                      width: 200,
                      render: (record) => {
                        const { coid } = record;
                        
                        return exportState ? coid : <Text>{coid}</Text>;
                      },
                    },
                  ]
                : []),
          {
            title: TABLE_BILLING_TITLE.BillingMonth,
            key: TABLE_BILLING_INDEX.BillingMonth,
            render: (record) => {
              const { billing_month } = record;
              return exportState ? (
                parseMonth(billing_month)
              ) : (
                <Text>{parseMonth(billing_month)}</Text>
              );
            },
            filters: filterHDLR.getBillingMonthFilters(filterBillingMonthData),
            onFilter: (value, record) => parseMonth(record.billing_month) === value,
            filterSearch: true,
          }
        ]
      : []),

    {
      title: TABLE_BILLING_TITLE.BillingDate,
      key: TABLE_BILLING_INDEX.BillingDate,
      render: (record) => {
        const { billing_date } = record;

        return exportState ? (
          parseDate(billing_date)
        ) : (
          <>{parseDate(billing_date)}</>
        );
      },
      sorter: true,
    },
    {
      title: TABLE_BILLING_TITLE.DueDate,
      key: TABLE_BILLING_INDEX.DueDate,
      render: (record) => {
        const { due_date } = record;
        const isOverdue = due_date < Date.now();
        return exportState ? (
          parseDate(due_date)
        ) : (
          <>
            {parseDate(due_date)} <br />{" "}
            {isOverdue && (
              <div
                style={{
                  color: "red",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "3px",
                }}
              >
                {" "}
                <img src={icon_history} alt="" width="15px" />{" "}
                <span>Overdue</span>{" "}
              </div>
            )}
          </>
        );
      },
      sorter: true,
    },
    {
      title: TABLE_BILLING_TITLE.Overdue,
      key: TABLE_BILLING_INDEX.Overdue,
      render: (record) => {
        const { due_date } = record;
        const isOverdue = due_date < Date.now();
        const diffMilliseconds = Date.now() - due_date;
        const diffDays = Math.floor(diffMilliseconds / (1000 * 60 * 60 * 24));
        return exportState ? (
          parseDate(due_date)
        ) : isOverdue ? (
          <span>{diffDays} days</span>
        ) : (
          <span> - </span>
        );
      },
    },
    {
      title: TABLE_BILLING_TITLE.BillingPeriod,
      key: TABLE_BILLING_INDEX.BillingPeriod,
      render: (record) => {
        const { billing_period } = record;

        const billing_period_start = billing_period?.start
          ? convertTimeFormat(billing_period.start)
          : " ";
        const billing_period_end = billing_period?.end
          ? convertTimeFormat(billing_period.end)
          : " ";

        const periodInfo =
          billing_period_start && billing_period_end
            ? `${billing_period_start} - ${billing_period_end}`
            : "";

        return exportState ? (
          periodInfo
        ) : (
          <>
            {billing_period_start} - {billing_period_end}
          </>
        );
      },
      // sorter: (a, b) => a.billing_period_start - b.billing_period_start,
    },
    {
      title: TABLE_BILLING_TITLE.BillingAmount,
      key: TABLE_BILLING_INDEX.BillingAmount,
      render: (record) => {
        const { amount, currency } = record;

        return exportState ? (
          amount
        ) : (
          <>
            {currency} {amount}
          </>
        );
      },
      sorter: true,
    },
    {
      title: TABLE_BILLING_TITLE.BillingAttachment,
      key: TABLE_BILLING_INDEX.BillingAttachment,
      render: (record) => {
        const { attachment } = record;

        const handleButtonClick = (file) => {window.open(file, "_blank");};

        return ( 
          <div
            style={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {attachment?.debitNote ? (
              <ButtonLink onClick={() => handleButtonClick(attachment?.debitNote)}>
              <Tooltip placement="top" title="Debit Note">
                <img
                  src={icon_pdf}
                  alt=""
                  style={{ width: "29px", height: "35px" }}
                />
              </Tooltip>
              </ButtonLink>
            ) : null}
            {attachment?.payrolladvsum ? (
              <ButtonLink onClick={() => handleButtonClick(attachment?.payrolladvsum )}>
              <Tooltip placement="top" title="Payroll Advance Summary">
                <img
                  src={icon_attch_excel}
                  alt=""
                  style={{ width: "29px", height: "35px" }}
                />
              </Tooltip>
              </ButtonLink>
            ) : null}
            {attachment?.payrolladvlist ? (
              <ButtonLink onClick={() => handleButtonClick(attachment?.payrolladvlist )}>
              <Tooltip placement="top" title="Payroll Advance List">
                <img
                  src={icon_attch_excel}
                  alt=""
                  style={{ width: "29px", height: "35px" }}
                />
              </Tooltip>
              </ButtonLink>
            ) : null}
          </div>
        );
      },
    },
    {
      title: TABLE_BILLING_TITLE.ReceiptRefID,
      key: TABLE_BILLING_INDEX.ReceiptRefID,
      render: (record) => {
        const { receipt, status } = record;

        const refidObject = receipt?.reduce((acc, item, index) => {
          acc[`refid${index + 1}`] = item.refid;
          return acc;
        }, {});

        let type = "";
        if (status === 2 || status === 3) {
          type = "update";
        } else {
          type = "view2";
        }

        const btnAction = isUserA ? (
          refidObject && Object.values(refidObject).length ? (
            <ButtonLink
              onClick={() => {
                DrawerManageReceipt(record, type);
              }}
            >
              View
            </ButtonLink>
          ) : (
            (status === 2 || status === 3) && (
              <ButtonLink
                onClick={() => {
                  DrawerManageReceipt(record, type);
                }}
              >
                Add
              </ButtonLink>
            )
          )
        ) : status !== 2 ? (
          <ButtonLink
            onClick={() => {
              DrawerView(record, "view");
            }}
          >
            View
          </ButtonLink>
        ) : (
          <ButtonLink
            onClick={() => {
              DrawerView(record, "pay");
            }}
          >
            Add
          </ButtonLink>
        );

        return exportState ? refidObject : btnAction;
      },
      // ...searchHDLR.getColumnSearchProps("refid", handleSearch),
    },
    {
      title: TABLE_BILLING_TITLE.UpdatedAt,
      key: TABLE_BILLING_INDEX.UpdatedAt,
      render: (record) => {
        const { updatedBy, updatedBy_name, updatedAt } = record;
        const updated = updatedBy_name ? `${updatedBy_name} /${updatedBy}` : `${updatedBy}`
        return exportState ? (
          `${updated} - ${parseTimeMin(updatedAt)}`
        ) : (
          <>
            {updated}
            <br />
            {parseTimeMin(updatedAt)}
          </>
        );
      },
      sorter: true,
    },
    {
      title: TABLE_BILLING_TITLE.CreatedAt,
      key: TABLE_BILLING_INDEX.CreatedAt,
      render: (record) => {
        const { createdAt, billedBy } = record;
        return exportState ? (
          (billedBy, parseTimeMin(createdAt))
        ) : (
          <span>
            {billedBy}
            <br />
            {parseTimeMin(createdAt)}
          </span>
        );
      },
      sorter: true,
    },
    {
      title: TABLE_BILLING_TITLE.Remarks,
      key: TABLE_BILLING_INDEX.Remarks,
      fixed: "right",
      render: (record) => {
        const { remarkCount } = record;

        return (
          <Badge
            count={remarkCount || 0}
            showZero
            offset={[10, 10]}
            color="#0E2483"
          >
            <ButtonLink
              onClick={() => {
                DrawerRemark(record);
              }}
            >
              <img src={icon_comment} alt="" />
            </ButtonLink>
          </Badge>
        );
      },
    },
  ];


  return columns;
};